import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IIndexTypeGroup } from "../../indexTypes/model/IndexTypeGroup";
import CreateOrganisationIndexGroupInteractor from "../interactors/CreateOrganisationIndexGroupInteractor";
import CreateOrganisationIndexInteractor from "../interactors/CreateOrganisationIndexInteractor";
import ConnectIndexesInteractor from "../interactors/ConnectIndexesInteractor";
import GetIndexTypeGroupTreeInteractor from "../interactors/GetIndexTypeGroupTreeInteractor";
import { IOrganisationIndex } from "../model/OrganisationIndex";
import { IOrganisationIndexGroup } from "../model/OrganisationIndexGroup";
import _ from "lodash";
import FindConnectionCandidatesForIndexInteractor from "../interactors/FindConnectionCandidatesForIndexInteractor";
import FindConnectionCandidatesForGroupInteractor from "../interactors/FindConnectionCandidatesForGroupInteractor";
import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "./model/OrganisationIndexPresentationModel";
import OrganisationIndexGroupPresentationModel, {
  TOrganisationIndexGroupPresentationModel
} from "./model/OrganisationIndexGroupPresentationModel";
import { IOrganisation } from "../model/Organisation";
import { IIndexType } from "../../indexTypes/model/IndexType";
import ShowInfoMessageInteractor from "../../core/interactor/notifications/ShowInfoMessageInteractor";
import FindConnectedIndexesInteractor from "../interactors/FindConnectedIndexesInteractor";
import FindConnectedIndexGroupsInteractor from "../interactors/FindConnectedIndexGroupsInteractor";
import IndexTypeGroupPresentationModel, {
  TIndexTypeGroupPresentationModel
} from "./model/IndexTypeGroupPresentationModel";
import NameTranslation from "../../core/model/translations/NameTranslation";
import UpdateOrganisationIndexInteractor from "../interactors/UpdateOrganisationIndexInteractor";
import UpdateOrganisationIndexGroupInteractor from "../interactors/UpdateOrganisationIndexGroupInteractor";
import ConnectIndexGroupsInteractor from "../interactors/ConnectIndexGroupsInteractor";
import RemoveOrganisationIndexInteractor from "../interactors/RemoveOrganisationIndexInteractor";
import GetCaseLawDocumentsByIndexInteractor from "../interactors/GetCaseLawDocumentsByIndexInteractor";
import { IUser } from "../../user/model/User";
import CaseLawDocumentPresentationModel, {
  TCaseLawDocumentPresentationModel
} from "../../caseLawDocuments/presenter/model/CaseLawDocumentPresentationModel";
import FindSwitchGroupCandidatesForIndexInteractor from "../interactors/FindSwitchGroupCandidatesForIndexInteractor";
import SwitchGroupForOrganisationIndexInteractor from "../interactors/SwitchGroupForOrganisationIndexInteractor";
import RemoveOrganisationIndexGroupInteractor from "../interactors/RemoveOrganisationIndexGroupInteractor";
import GetConnectionCandidatesByCategoryInteractor from "../interactors/GetConnectionCandidatesByCategoryInteractor";
import ConnectIndexesByCategoryInteractor from "../interactors/ConnectIndexesByCategoryInteractor";
import GetConnectedIndexesByCategoryInteractor from "../interactors/GetConnectedIndexesByCategoryInteractor";

export interface TOrganisationIndexGroupPresenter
  extends TLoadingAwarePresenter {
  organisationIndexDataTree: TIndexTypeGroupPresentationModel[];
  searchableOrganisationIndexDataTree: TIndexTypeGroupPresentationModel[];
  organisationId: number;
  categoryId: number;
  indexTypeGroupId: number;
  indexTypeId: number;
  createGroupModalVisible: boolean;
  createItemModalVisible: boolean;
  indexesModalVisible: boolean;
  groupsModalVisible: boolean;
  updateIndexModalVisible: boolean;
  updateIndexGroupModalVisible: boolean;
  caseLawDocsModalVisible: boolean;
  deleteWarningModalVisible: boolean;
  switchGroupVisible: boolean;
  deleteGroupModalVisible: boolean;
  deleteGroupWarningVisible: boolean;
  organisationTypeGroupId: number;
  indexesNotInOrganisation: TOrganisationIndexPresentationModel[];
  groupModelsCandidates: TOrganisationIndexGroupPresentationModel[];
  groupsCandidates: IOrganisationIndexGroup[];
  organisations: IOrganisation[];
  indexTypes: IIndexType[];
  selectedIndexes: TOrganisationIndexPresentationModel[];
  selectedIndexesByCategory: TOrganisationIndexPresentationModel[];
  selectedGroups: TOrganisationIndexGroupPresentationModel[];
  indexInfoVisible: boolean;
  groupInfoVisible: boolean;
  organisationIndex?: IOrganisationIndex;
  clickedIndex?: TOrganisationIndexPresentationModel;
  clickedGroup?: IOrganisationIndexGroup;
  hierarchyList?: string[];
  indexData?: IOrganisationIndex;
  groupData?: IOrganisationIndexGroup;
  connectedIndexes?: TOrganisationIndexPresentationModel[];
  connectedIndexGroups?: TOrganisationIndexGroupPresentationModel[];
  connectionCandidates?: IOrganisationIndex[];
  connectionCandidatesByCategory?: TOrganisationIndexPresentationModel[];
  filterByParentNameChecked: boolean;
  filterGroupByParentNameChecked: boolean;
  indexTypeGroups: IIndexTypeGroup[];
  allGroups: IOrganisationIndexGroup[];
  user?: IUser;
  caseLawDocuments?: TCaseLawDocumentPresentationModel[];
  switchCandidates?: IOrganisationIndexGroup[];
  selectedSwitchCandidate?: IOrganisationIndexGroup;
  canSelectedIndexBeNonHarmonized?: boolean;
  connectedIndexesByCategory: TOrganisationIndexPresentationModel[];
}

export interface IOrganisationIndexGroupPresenter
  extends TOrganisationIndexGroupPresenter,
    TPresentable {
  loadData(data: IIndexTypeGroup[]): void;
  loadReferenceValues(
    organisationId: number,
    categoryId: number,
    organisations: IOrganisation[],
    indexTypes: IIndexType[],
    indexTypeGroups: IIndexTypeGroup[],
    allGroups: IOrganisationIndexGroup[]
  ): void;
  createOrganisationIndexGroup(data: IOrganisationIndexGroup): Promise<boolean>;
  createOrganisationIndex(data: IOrganisationIndex): Promise<boolean>;
  changeIndexesModalVisible(value: boolean): void;
  changeGroupsModalVisible(value: boolean): void;
  changeCreateGroupModalVisible(
    value: boolean,
    indexTypeGroupId: number,
    organisationTypeGroupId: number
  ): void;
  changeCreateItemModalVisible(
    value: boolean,
    indexTypeId: number,
    organisationTypeGroupId: number
  ): void;
  changeUpdateIndexModalVisible(value: boolean): void;
  changeUpdateIndexGroupModalVisible(updateIndexGroupModalVisible: boolean);
  searchDataTree(searchValue): void;
  findConnectionCandidatesForIndex(data: IOrganisationIndex): Promise<boolean>;
  findConnectionCandidatesForGroup(
    data: IOrganisationIndexGroup
  ): Promise<boolean>;
  toggleConnected(index: TOrganisationIndexPresentationModel, e: any): void;
  toggleGroupConnected(
    index: TOrganisationIndexGroupPresentationModel,
    e: any
  ): void;
  connectIndexes(index?: TOrganisationIndexPresentationModel): void;
  connectGroups(group?: TOrganisationIndexGroupPresentationModel): void;
  selectOrganisationIndex(
    clickedIndex: TOrganisationIndexPresentationModel
  ): void;
  selectOrganisationIndexGroup(group: IOrganisationIndexGroup): void;
  showIndexInfo(): void;
  showGroupInfo(): void;
  updateOrganisationIndex(organisationIndex: IOrganisationIndex): void;
  updateOrganisationIndexGroup(
    organisationIndexGroup: IOrganisationIndexGroup
  ): void;
  searchFunction(
    indexes: TOrganisationIndexPresentationModel[],
    searchValue: string
  ): TOrganisationIndexPresentationModel[];
  searchGroupFunction(
    groups: TOrganisationIndexGroupPresentationModel[],
    searchValue: string
  ): TOrganisationIndexGroupPresentationModel[];
  toggleFilterByParentName(value: any): void;
  toggleGroupFilterByParentName(checked: boolean): void;
  canEdit(organisationId: number): boolean;
  getCaseLawDocumentsForIndex(organisationIndex: IOrganisationIndex): void;
  removeOrganisationIndex(organisationIndex: IOrganisationIndex): void;
  changeCaseLawDocsModalVisible(): void;
  changeDeleteWarningModalVisible(): void;
  showCaseLawDocumentDetails(id: number): void;
  getSwitchGroupCandidates(): void;
  toggleSwitchGroup(group: TOrganisationIndexGroupPresentationModel): void;
  changeSwitchGroupVisible(): void;
  switchGroupForIndex(organisationIndex: IOrganisationIndex): void;
  searchSwitchGroupFunction(
    groups: TOrganisationIndexGroupPresentationModel[],
    searchValue: string
  ): TOrganisationIndexGroupPresentationModel[];
  removeOrganisationIndexGroup(): void;
  changeDeleteGroupVisible(): void;
  changeDeleteGroupWarningVisible(): void;
  checkCanGroupBeRemoved(group: IOrganisationIndexGroup): void;
  findConnectionCandidatesByCategory(index: IOrganisationIndex): void;
  toggleConnectedByCategory(indexIds: number[]): void;
}

const defaultState: TOrganisationIndexGroupPresenter = {
  organisationIndexDataTree: [],
  organisationId: NaN,
  categoryId: NaN,
  indexTypeGroupId: NaN,
  indexTypeId: NaN,
  organisationTypeGroupId: NaN,
  createGroupModalVisible: false,
  createItemModalVisible: false,
  indexesModalVisible: false,
  groupsModalVisible: false,
  searchableOrganisationIndexDataTree: [],
  indexesNotInOrganisation: [],
  groupModelsCandidates: [],
  groupsCandidates: [],
  organisations: [],
  indexTypes: [],
  selectedIndexes: [],
  selectedGroups: [],
  organisationIndex: undefined,
  clickedIndex: undefined,
  indexInfoVisible: false,
  groupInfoVisible: false,
  updateIndexModalVisible: false,
  updateIndexGroupModalVisible: false,
  connectionCandidates: [],
  connectionCandidatesByCategory: [],
  filterByParentNameChecked: false,
  filterGroupByParentNameChecked: false,
  indexTypeGroups: [],
  allGroups: [],
  caseLawDocuments: [],
  caseLawDocsModalVisible: false,
  deleteWarningModalVisible: false,
  switchCandidates: [],
  switchGroupVisible: false,
  deleteGroupModalVisible: false,
  deleteGroupWarningVisible: false,
  selectedIndexesByCategory: [],
  connectedIndexesByCategory: []
};

const OrganisationIndexGroupPresenter = withStore<
  IOrganisationIndexGroupPresenter,
  TOrganisationIndexGroupPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IOrganisationIndexGroupPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TOrganisationIndexGroupPresenter & TLoadingAwarePresenter
    >();
    const loadReferenceValues = (
      organisationId: number,
      categoryId: number,
      organisations: IOrganisation[],
      indexTypes: IIndexType[],
      indexTypeGroups: IIndexTypeGroup[],
      allGroups: IOrganisationIndexGroup[]
    ) => {
      const user = _store.getState<any>().user;
      _store.update({
        organisationId,
        categoryId,
        organisations,
        indexTypes,
        indexTypeGroups,
        allGroups,
        user
      });
    };
    loader.start("organisationIndexGroupsLoader");
    const loadData = (data: IIndexTypeGroup[]) => {
      const { selectedLanguage } = _store.getState<any>();
      _store.update({
        organisationIndexDataTree: data.map((item) =>
          IndexTypeGroupPresentationModel(item, selectedLanguage)
        ),
        searchableOrganisationIndexDataTree: data.map((item) =>
          IndexTypeGroupPresentationModel(item, selectedLanguage)
        )
      });
      loader.stop("organisationIndexGroupsLoader");
    };
    const canEdit = () => {
      const { user, organisationId } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (user) {
        return user.canEdit(organisationId);
      } else {
        return false;
      }
    };
    const getAllOrganisationIndexGroups = async () => {
      const { organisationId, categoryId } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      let groups = await _application.container
        .resolve<GetIndexTypeGroupTreeInteractor>("getIndexTypeGroupTree")
        .execute(organisationId, categoryId);
      loadData(groups);
    };
    const createOrganisationIndexGroup = async (data: any) => {
      loader.start("organisationIndexGroupsLoader");
      const { selectedLanguage } = _store.getState<any>();
      let translations = data.nameTranslations.map((item) => {
        var element = NameTranslation(item);
        element.organisationId = data.organisationId;
        return element;
      });
      let originalName = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      data.name = originalName?.nameTranslation ?? "";
      data.nameTranslations = translations;
      await _application.container
        .resolve<CreateOrganisationIndexGroupInteractor>(
          "createOrganisationIndexGroup"
        )
        .execute(data);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("organisationIndexGroupsLoader");
      await getAllOrganisationIndexGroups();
      return true;
    };

    const createOrganisationIndex = async (data: IOrganisationIndex) => {
      loader.start("organisationIndexGroupsLoader");
      const { selectedLanguage } = _store.getState<any>();
      let translations = data.nameTranslations.map((item) => {
        var element = NameTranslation(item);
        element.organisationId = data.organisationId;
        return element;
      });
      let originalName = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      data.name = originalName?.nameTranslation ?? "";
      data.nameTranslations = translations;
      if (!data.organisationId) {
        let { organisationIndex } =
          _store.getState<TOrganisationIndexGroupPresenter>();
        if (organisationIndex !== undefined) {
          organisationIndex.name = originalName?.nameTranslation ?? "";
          organisationIndex.nameTranslations = translations;

          await _application.container
            .resolve<CreateOrganisationIndexInteractor>(
              "createOrganisationIndex"
            )
            .execute(organisationIndex);
        }
      } else {
        data = await _application.container
          .resolve<CreateOrganisationIndexInteractor>("createOrganisationIndex")
          .execute(data);
        _store.update({ indexData: data });
      }
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      if (data.harmonized === false) {
        findConnectionCandidatesForIndex(data);
      }
      loader.stop("organisationIndexGroupsLoader");
      await getAllOrganisationIndexGroups();
      return true;
    };

    const preparePresentationModels = (list: IOrganisationIndex[]) => {
      const { selectedLanguage } = _store.getState<any>();
      const { organisations, indexTypes } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      return list.map((item) => {
        let organisation = organisations.find(
          (o) => o.id === item.organisationId
        );
        let indexType = indexTypes.find((t) => t.id === item.indexTypeId);
        let hierarchy = createHierarchyList(item);
        return OrganisationIndexPresentationModel(
          item,
          organisation,
          indexType,
          false,
          hierarchy,
          selectedLanguage
        );
      });
    };

    const prepareGroupsPresentationModels = (
      list: IOrganisationIndexGroup[]
    ) => {
      const { selectedLanguage } = _store.getState<any>();
      const { organisations, indexTypeGroups } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      return list.map((item) => {
        let organisation = organisations.find(
          (o) => o.id === item.organisationId
        );
        let indexTypeGroup = indexTypeGroups.find(
          (t) => t.id === item.indexTypeGroupId
        );
        let group = OrganisationIndexGroupPresentationModel(
          item,
          selectedLanguage,
          organisation,
          indexTypeGroup,
          false
        );
        return group;
      });
    };

    const createHierarchyList = (index: IOrganisationIndex) => {
      let indexHierarchy: string[] = [];
      let firstGroup = index?.groups?.find(
        (g) => g.id === index.organisationGroupId
      );

      indexHierarchy.push(getCorrectIndexTranslation(index));
      indexHierarchy.push(getCorrectGroupTranslation(firstGroup));
      indexHierarchy = findGroupParent(
        firstGroup,
        index.groups,
        indexHierarchy
      );
      return indexHierarchy;
    };
    const getCorrectIndexTranslation = (index: IOrganisationIndex) => {
      const { selectedLanguage } = _store.getState<any>();
      const nameTranslation = index.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? index.name;
    };
    const getCorrectGroupTranslation = (data?: IOrganisationIndexGroup) => {
      const { selectedLanguage } = _store.getState<any>();
      if (data) {
        const nameTranslation = data.nameTranslations.find(
          (item) => item.languageId === selectedLanguage?.id
        )?.nameTranslation;
        return nameTranslation ?? data.name;
      } else {
        return "";
      }
    };

    const findGroupParent = (group, groupList, indexHierarchyList) => {
      if (group?.parentId) {
        let g = groupList.find((groupItem) => groupItem.id === group?.parentId);
        if (g) {
          indexHierarchyList.push(getCorrectGroupTranslation(g));
          return findGroupParent(g, groupList, indexHierarchyList);
        }
      } else if (group?.organisationIndexCategoryId) {
        indexHierarchyList.push(group?.categoryName);
      }
      return indexHierarchyList;
    };

    const findConnectionCandidatesByCategory = async (
      index: IOrganisationIndex
    ) => {
      loader.start("organisationIndexGroupsLoader");
      let candidates = await _application.container
        .resolve<GetConnectionCandidatesByCategoryInteractor>(
          "GetConnectionCandidatesByCategory"
        )
        .execute(index);
      let connectedIndexes = await _application.container
        .resolve<GetConnectedIndexesByCategoryInteractor>(
          "getConnectedIndexesByCategory"
        )
        .execute(index);

      let candidateModels = preparePresentationModels(candidates);
      let connectedModels = preparePresentationModels(connectedIndexes);
      if (candidates.length > 0) {
        _store.update({
          connectionCandidatesByCategory: candidateModels,
          connectedIndexesByCategory: connectedModels
        });
      } else {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(translate("editors.organisation_indexes.no_indexes"));
      }

      loader.stop("organisationIndexGroupsLoader");
      return true;
    };

    const findConnectionCandidatesForIndex = async (
      data: IOrganisationIndex
    ) => {
      loader.start("organisationIndexGroupsLoader");
      let list = await _application.container
        .resolve<FindConnectionCandidatesForIndexInteractor>(
          "findConnectionCandidatesForIndex"
        )
        .execute(data);
      _store.update({ organisationIndex: data });
      let models = preparePresentationModels(list);
      if (list.length > 0) {
        _store.update({
          indexesNotInOrganisation: models,
          connectionCandidates: list
        });
        changeIndexesModalVisible(true);
      } else {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(translate("editors.organisation_indexes.no_indexes"));
      }
      loader.stop("organisationIndexGroupsLoader");
      return true;
    };

    const findConnectionCandidatesForGroup = async (
      data: IOrganisationIndexGroup
    ) => {
      loader.start("organisationIndexGroupsLoader");
      let list = await _application.container
        .resolve<FindConnectionCandidatesForGroupInteractor>(
          "findConnectionCandidatesForGroup"
        )
        .execute(data);
      if (list.length > 0) {
        let models = prepareGroupsPresentationModels(list);
        _store.update({
          groupsCandidates: list,
          groupModelsCandidates: models
        });
        changeGroupsModalVisible(true);
      } else {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(translate("editors.organisation_indexes.no_index_groups"));
      }
      loader.stop("organisationIndexGroupsLoader");
      return true;
    };

    const changeCreateGroupModalVisible = (
      value: boolean,
      indexTypeGroupId: number,
      organisationTypeGroupId: number
    ) => {
      _store.update({
        createGroupModalVisible: value,
        indexTypeGroupId,
        organisationTypeGroupId
      });
    };
    const changeCreateItemModalVisible = (
      value: boolean,
      indexTypeId: number,
      organisationTypeGroupId: number
    ) => {
      _store.update({
        createItemModalVisible: value,
        indexTypeId,
        organisationTypeGroupId
      });
    };
    const changeIndexesModalVisible = (value: boolean) => {
      _store.update({
        indexesModalVisible: value
      });
      if (!value) {
        const { clickedIndex } =
          _store.getState<TOrganisationIndexGroupPresenter>();
        selectOrganisationIndex(clickedIndex);
      }
    };
    const changeGroupsModalVisible = (value: boolean) => {
      if (!value) {
        const { clickedGroup } =
          _store.getState<TOrganisationIndexGroupPresenter>();
        clickedGroup && selectOrganisationIndexGroup(clickedGroup);
      }
      _store.update({
        groupsModalVisible: value
      });
    };
    const findParent = (groups, groupList, result) => {
      groups?.forEach((group) => {
        if (group?.parentId) {
          let g = groupList.find((g) => g.id === group?.parentId);

          if (g) {
            let grop = [g];

            result.push(g);

            return findParent(grop, groupList, result);
          }
        }
        return result;
      });

      return result;
    };
    const filter = (text: string) => {
      const { organisationIndexDataTree } =
        _store.getState<TOrganisationIndexGroupPresenter>();

      let filteredTree = _.cloneDeep(organisationIndexDataTree);
      const getNodes = (result, object) => {
        if (Array.isArray(object.organisationIndexGroupList)) {
          const organisationIndexGroupList =
            object.organisationIndexGroupList.filter(
              (item) =>
                (item.nameTranslation ?? item.name)
                  .toLowerCase()
                  .indexOf(text.toLowerCase()) !== -1
            );

          if (organisationIndexGroupList.length > 0) {
            object.organisationIndexGroupList = findParent(
              organisationIndexGroupList,
              object.organisationIndexGroupList,
              organisationIndexGroupList
            );
            result.push(object);
            return result;
          } else {
            let groupList: IOrganisationIndexGroup[] = [];
            object.organisationIndexGroupList?.forEach((elem, index) => {
              const organisationIndexList = elem.organisationIndexList.filter(
                (item) =>
                  (item.nameTranslation ?? item.name)
                    .toLowerCase()
                    .indexOf(text.toLowerCase()) !== -1
              );
              if (organisationIndexList.length > 0) {
                elem.organisationIndexList = organisationIndexList;
                groupList.push(elem);
              }
            });
            if (groupList && groupList.length > 0) {
              object.organisationIndexGroupList = findParent(
                [groupList[0]],
                object.organisationIndexGroupList,
                groupList
              );

              result.push(object);
              return result;
            }
          }
        }

        if (Array.isArray(object.childGroups)) {
          const childGroups = object.childGroups.reduce(getNodes, []);

          if (childGroups.length) result.push({ ...object, childGroups });
        }
        return result;
      };

      return filteredTree.reduce(getNodes, []);
    };
    const searchDataTree = (searchValue) => {
      var searchableOrganisationIndexDataTree = filter(searchValue);

      _store.update({ searchableOrganisationIndexDataTree });
    };

    const toggleConnected = (
      index: TOrganisationIndexPresentationModel,
      checked: boolean
    ) => {
      const { selectedIndexes } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (checked) {
        (index as any).connected = true;
        selectedIndexes.push(index);
        _store.update({ selectedIndexes });
      } else {
        let filtered = selectedIndexes.filter(function (item) {
          return item !== index;
        });
        _store.update({
          selectedIndexes: filtered
        });
      }
    };

    const toggleGroupConnected = (
      group: TOrganisationIndexGroupPresentationModel,
      checked: boolean
    ) => {
      const { selectedGroups } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (checked) {
        selectedGroups.push(group);
        _store.update({ selectedGroups });
      } else {
        let filtered = selectedGroups.filter(function (item) {
          return item !== group;
        });
        _store.update({
          selectedIndexes: filtered
        });
      }
    };

    const toggleConnectedByCategory = (indexIds: number[]) => {
      const { connectionCandidatesByCategory } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      let selectedIndexesByCategory = connectionCandidatesByCategory?.filter(
        (candidate) => {
          if (indexIds.find((i) => i === candidate.id)) {
            return candidate;
          }
        }
      );
      _store.update({
        selectedIndexesByCategory
      });
    };

    const connectIndexes = async (
      index?: TOrganisationIndexPresentationModel
    ) => {
      loader.start("organisationIndexGroupsLoader");
      let {
        selectedIndexes,
        organisationId,
        indexData,
        clickedIndex,
        connectionCandidates
      } = _store.getState<TOrganisationIndexGroupPresenter>();
      if (index?.id) {
        selectedIndexes = [index];
      }
      let data = clickedIndex ? clickedIndex : indexData;
      let candidates = connectionCandidates?.filter((index) => {
        if (selectedIndexes.find((i) => i.id === index.id)) {
          return index;
        }
      });
      if (data && candidates) {
        await _application.container
          .resolve<ConnectIndexesInteractor>("connectIndexes")
          .execute(data.id, candidates, organisationId);
      }
      _store.update({ selectedIndexes: [] });
      loader.stop("organisationIndexGroupsLoader");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
    };

    const connectGroups = async (
      group?: TOrganisationIndexGroupPresentationModel
    ) => {
      loader.start("organisationIndexGroupsLoader");
      let {
        selectedGroups,
        organisationId,
        groupData,
        clickedGroup,
        groupsCandidates
      } = _store.getState<TOrganisationIndexGroupPresenter>();
      if (group?.id) {
        selectedGroups = [group];
      }
      let data = clickedGroup ? clickedGroup : groupData;
      let candidates = groupsCandidates?.filter((g) => {
        if (selectedGroups.find((i) => i.id === g.id)) {
          return g;
        }
      });
      if (data && candidates) {
        await _application.container
          .resolve<ConnectIndexGroupsInteractor>("connectIndexGroups")
          .execute(data.id, candidates, organisationId);
      }
      _store.update({ selectedGroups: [] });
      loader.stop("organisationIndexGroupsLoader");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
    };

    const selectOrganisationIndex = (index: any) => {
      if (!index) {
        return;
      }
      const { organisations, indexTypes, searchableOrganisationIndexDataTree } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      let organisationIndexGroup = searchableOrganisationIndexDataTree
        .map((item) =>
          item.childGroups
            .map((cg) =>
              cg.organisationIndexGroupList.find(
                (g) => g.id === index.organisationGroupId
              )
            )
            .find((a) => a !== undefined)
        )
        .find((a) => a !== undefined);
      let canSelectedIndexBeNonHarmonized = !organisationIndexGroup?.harmonized;
      if (index?.organisation) {
        _store.update({ clickedIndex: index, canSelectedIndexBeNonHarmonized });
      } else {
        let organisation = organisations.find(
          (o) => o.id === index?.organisationId
        );
        let indexType = indexTypes.find((t) => t.id === index?.indexTypeId);
        if (index) {
          let indexTypeModel = OrganisationIndexPresentationModel(
            index,
            organisation,
            indexType,
            false
          );
          _store.update({
            clickedIndex: indexTypeModel
          });
        }
      }
      index && findConnectionCandidatesByCategory(index);

      showIndexInfo();
      getConnectedIndexes();
    };

    const selectOrganisationIndexGroup = (group: any) => {
      const { organisations, indexTypeGroups } =
        _store.getState<TOrganisationIndexGroupPresenter>();

      if (group?.organisation) {
        _store.update({ clickedGroup: group, clickedIndex: undefined });
      } else {
        let organisation = organisations.find(
          (o) => o.id === group?.organisationId
        );
        let groupType = indexTypeGroups.find(
          (t) => t.id === group.indexTypeGroupId
        );
        if (group) {
          let groupTypeModel = OrganisationIndexGroupPresentationModel(
            group,
            undefined,
            organisation,
            groupType
          );
          _store.update({
            clickedGroup: groupTypeModel,
            clickedIndex: undefined
          });
        }
      }
      showGroupInfo();
      getConnectedIndexGroups();
    };

    const getConnectedIndexes = async () => {
      let { clickedIndex } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      loader.start("organisationIndexGroupsLoader");
      if (clickedIndex) {
        let connectedIndexes = await _application.container
          .resolve<FindConnectedIndexesInteractor>("findConnectedIndexes")
          .execute(clickedIndex.id, clickedIndex.organisationId);
        let list = preparePresentationModels(connectedIndexes);
        _store.update({
          connectedIndexes: list
        });
        loader.stop("organisationIndexGroupsLoader");
      }
    };

    const getConnectedIndexGroups = async () => {
      let { clickedGroup } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      loader.start("organisationIndexGroupsLoader");
      if (clickedGroup) {
        let connectedIndexGroups = await _application.container
          .resolve<FindConnectedIndexGroupsInteractor>(
            "findConnectedIndexGroups"
          )
          .execute(clickedGroup.id, clickedGroup.organisationId);
        let list = prepareGroupsPresentationModels(connectedIndexGroups);
        _store.update({
          connectedIndexGroups: list
        });
        loader.stop("organisationIndexGroupsLoader");
      }
    };

    const showIndexInfo = async () => {
      _store.update({
        indexInfoVisible: true,
        groupInfoVisible: false
      });
    };

    const showGroupInfo = async () => {
      _store.update({
        groupInfoVisible: true,
        indexInfoVisible: false
      });
    };

    const changeUpdateIndexModalVisible = async (
      updateIndexModalVisible: boolean
    ) => {
      _store.update({
        updateIndexModalVisible,
        selectedIndexes: []
      });
    };

    const updateOrganisationIndex = async (
      organisationIndex: IOrganisationIndex
    ) => {
      loader.start("organisationIndexGroupsLoader");

      const { selectedLanguage, selectedIndexesByCategory } =
        _store.getState<any>();
      let translations = organisationIndex.nameTranslations.map((item) => {
        var element = NameTranslation(item);
        element.organisationId = organisationIndex.organisationId;
        return element;
      });
      let originalName = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      originalName = originalName ? originalName : translations[0];
      organisationIndex.name = originalName?.nameTranslation ?? "";
      organisationIndex.nameTranslations = translations;
      await _application.container
        .resolve<ConnectIndexesByCategoryInteractor>("connectIndexesByCategory")
        .execute(
          organisationIndex.id,
          selectedIndexesByCategory,
          organisationIndex.organisationId
        );
      await _application.container
        .resolve<UpdateOrganisationIndexInteractor>("updateOrganisationIndex")
        .execute(organisationIndex);
      await getAllOrganisationIndexGroups();
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      changeUpdateIndexModalVisible(false);
      selectOrganisationIndex(organisationIndex);
      loader.stop("organisationIndexGroupsLoader");
    };

    const changeUpdateIndexGroupModalVisible = async (
      updateIndexGroupModalVisible: boolean
    ) => {
      _store.update({
        updateIndexGroupModalVisible
      });
    };

    const updateOrganisationIndexGroup = async (
      organisationIndexGroup: IOrganisationIndexGroup
    ) => {
      loader.start("organisationIndexGroupsLoader");

      const { selectedLanguage } = _store.getState<any>();
      let translations = organisationIndexGroup.nameTranslations.map((item) => {
        var element = NameTranslation(item);
        element.organisationId = organisationIndexGroup.organisationId;
        return element;
      });

      let originalName = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      originalName = originalName ? originalName : translations[0];
      organisationIndexGroup.name = originalName?.nameTranslation ?? "";
      organisationIndexGroup.nameTranslations = translations;
      await _application.container
        .resolve<UpdateOrganisationIndexGroupInteractor>(
          "updateOrganisationIndexGroup"
        )
        .execute(organisationIndexGroup);
      await getAllOrganisationIndexGroups();
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      changeUpdateIndexGroupModalVisible(false);
      selectOrganisationIndexGroup(organisationIndexGroup);

      loader.stop("organisationIndexGroupsLoader");
    };

    const searchFunction = (
      indexes: TOrganisationIndexPresentationModel[],
      searchValue: string
    ) => {
      let { connectionCandidates, filterByParentNameChecked } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (!filterByParentNameChecked) {
        let models =
          connectionCandidates &&
          preparePresentationModels(connectionCandidates).filter((i) =>
            i.name.toLowerCase().includes(searchValue.toLowerCase())
          );
        _store.update({ indexesNotInOrganisation: models });
        return models ? models : [];
      } else return indexes;
    };

    const searchGroupFunction = (
      groups: TOrganisationIndexGroupPresentationModel[],
      searchValue: string
    ) => {
      let { groupsCandidates, filterGroupByParentNameChecked } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (!filterGroupByParentNameChecked) {
        let models =
          groupsCandidates &&
          prepareGroupsPresentationModels(groupsCandidates).filter((i) =>
            i.name.toLowerCase().includes(searchValue.toLowerCase())
          );
        _store.update({ groupModelsCandidates: models });
        return models ? models : [];
      } else return groups;
    };

    const searchSwitchGroupFunction = (
      groups: TOrganisationIndexGroupPresentationModel[],
      searchValue: string
    ) => {
      let { switchCandidates } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      let models =
        switchCandidates &&
        prepareGroupsPresentationModels(switchCandidates).filter((i) =>
          i.name.toLowerCase().includes(searchValue.toLowerCase())
        );
      _store.update({ groupModelsCandidates: models });
      return models ? models : [];
    };

    const toggleFilterByParentName = (checked: boolean) => {
      const { clickedIndex, indexesNotInOrganisation, connectionCandidates } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (checked) {
        let parentName =
          clickedIndex &&
          clickedIndex.groups?.find(
            (g) => g.id === clickedIndex.organisationGroupId
          )?.name;
        let filteredList = indexesNotInOrganisation.filter((i) =>
          i.name.includes(parentName || "")
        );
        _store.update({
          indexesNotInOrganisation: filteredList,
          filterByParentNameChecked: checked
        });
      } else {
        let list = preparePresentationModels(connectionCandidates || []);
        _store.update({
          indexesNotInOrganisation: list,
          filterByParentNameChecked: checked
        });
      }
    };

    const toggleGroupFilterByParentName = (checked: boolean) => {
      const {
        clickedGroup,
        groupModelsCandidates,
        allGroups,
        groupsCandidates
      } = _store.getState<TOrganisationIndexGroupPresenter>();
      if (checked) {
        let parentName =
          allGroups &&
          allGroups.find((g) => g.id === clickedGroup?.parentId)?.name;
        let filteredList = groupModelsCandidates.filter((g) =>
          g.name.includes(parentName || "")
        );
        _store.update({
          groupModelsCandidates: filteredList,
          filterGroupByParentNameChecked: checked
        });
      } else {
        let list = prepareGroupsPresentationModels(groupsCandidates || []);
        _store.update({
          groupModelsCandidates: list,
          filterGroupByParentNameChecked: checked
        });
      }
    };

    const changeCaseLawDocsModalVisible = () => {
      const { caseLawDocsModalVisible } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      _store.update({ caseLawDocsModalVisible: !caseLawDocsModalVisible });
    };

    const changeDeleteWarningModalVisible = () => {
      const { deleteWarningModalVisible } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      _store.update({ deleteWarningModalVisible: !deleteWarningModalVisible });
    };

    const getCaseLawDocumentsForIndex = async (
      organisationIndex: IOrganisationIndex
    ) => {
      loader.start("organisationIndexGroupsLoader");
      const { organisationId } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      let data = await _application.container
        .resolve<GetCaseLawDocumentsByIndexInteractor>(
          "getCaseLawDocumentsByIndex"
        )
        .execute(organisationId, organisationIndex.id);
      _store.update({
        caseLawDocuments: data.map((item) =>
          CaseLawDocumentPresentationModel(item)
        )
      });
      if (data.length > 0) {
        changeCaseLawDocsModalVisible();
      } else {
        changeDeleteWarningModalVisible();
      }
      loader.stop("organisationIndexGroupsLoader");
    };

    const removeOrganisationIndex = async () => {
      loader.start("organisationIndexGroupsLoader");
      const { organisationId, clickedIndex } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (clickedIndex) {
        await _application.container
          .resolve<RemoveOrganisationIndexInteractor>("removeOrganisationIndex")
          .execute(organisationId, clickedIndex.id);
        getAllOrganisationIndexGroups();
        _store.update({ clickedIndex: undefined });
      }
      loader.stop("organisationIndexGroupsLoader");
    };

    const removeOrganisationIndexGroup = async () => {
      loader.start("organisationIndexGroupsLoader");
      const { organisationId, clickedGroup } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (clickedGroup) {
        await _application.container
          .resolve<RemoveOrganisationIndexGroupInteractor>(
            "removeOrganisationIndexGroup"
          )
          .execute(organisationId, clickedGroup.id);
        getAllOrganisationIndexGroups();
        _store.update({ clickedGroup: undefined });
      }
      loader.stop("organisationIndexGroupsLoader");
    };

    const checkCanGroupBeRemoved = (group: IOrganisationIndexGroup) => {
      if (
        (group.children && group.children?.length > 0) ||
        (group.groups && group.groups?.length > 0) ||
        group.organisationIndexList?.length > 0
      ) {
        changeDeleteGroupWarningVisible();
      } else {
        changeDeleteGroupVisible();
      }
    };

    const showCaseLawDocumentDetails = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-law-documents/${id.toString()}/details`
      });
    };

    const getSwitchGroupCandidates = async () => {
      loader.start("organisationIndexGroupsLoader");
      const { organisationId, clickedIndex } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (clickedIndex) {
        let data = await _application.container
          .resolve<FindSwitchGroupCandidatesForIndexInteractor>(
            "findSwitchGroupCandidatesForIndex"
          )
          .execute(clickedIndex?.id, organisationId);
        _store.update({
          switchCandidates: data.map((item) =>
            OrganisationIndexGroupPresentationModel(item)
          ),
          switchGroupVisible: true
        });
      }
      loader.stop("organisationIndexGroupsLoader");
    };

    const toggleSwitchGroup = (
      group: TOrganisationIndexGroupPresentationModel
    ) => {
      _store.update({ selectedSwitchCandidate: group });
    };

    const changeSwitchGroupVisible = () => {
      const { switchGroupVisible } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      _store.update({ switchGroupVisible: !switchGroupVisible });
    };

    const changeDeleteGroupVisible = () => {
      const { deleteGroupModalVisible } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      _store.update({ deleteGroupModalVisible: !deleteGroupModalVisible });
    };

    const changeDeleteGroupWarningVisible = () => {
      const { deleteGroupWarningVisible } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      _store.update({ deleteGroupWarningVisible: !deleteGroupWarningVisible });
    };

    const switchGroupForIndex = async (
      organisationIndex: IOrganisationIndex
    ) => {
      const { organisationId, selectedSwitchCandidate, clickedIndex } =
        _store.getState<TOrganisationIndexGroupPresenter>();
      if (selectedSwitchCandidate && clickedIndex) {
        organisationIndex.organisationGroupId = selectedSwitchCandidate.id;
        await _application.container
          .resolve<SwitchGroupForOrganisationIndexInteractor>(
            "switchGroupForOrganisationIndex"
          )
          .execute(
            organisationId,
            clickedIndex?.id,
            selectedSwitchCandidate?.id
          );
      }
      getAllOrganisationIndexGroups();
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadData,
      loadReferenceValues,
      createOrganisationIndexGroup,
      createOrganisationIndex,
      changeCreateGroupModalVisible,
      changeCreateItemModalVisible,
      changeIndexesModalVisible,
      changeGroupsModalVisible,
      changeUpdateIndexModalVisible,
      changeUpdateIndexGroupModalVisible,
      searchDataTree,
      findConnectionCandidatesForIndex,
      findConnectionCandidatesForGroup,
      findConnectionCandidatesByCategory,
      toggleConnected,
      toggleGroupConnected,
      connectIndexes,
      connectGroups,
      selectOrganisationIndex,
      showIndexInfo,
      showGroupInfo,
      updateOrganisationIndex,
      updateOrganisationIndexGroup,
      selectOrganisationIndexGroup,
      searchFunction,
      searchGroupFunction,
      toggleFilterByParentName,
      toggleGroupFilterByParentName,
      canEdit,
      getCaseLawDocumentsForIndex,
      removeOrganisationIndex,
      changeCaseLawDocsModalVisible,
      changeDeleteWarningModalVisible,
      showCaseLawDocumentDetails,
      getSwitchGroupCandidates,
      toggleSwitchGroup,
      changeSwitchGroupVisible,
      switchGroupForIndex,
      searchSwitchGroupFunction,
      removeOrganisationIndexGroup,
      changeDeleteGroupVisible,
      changeDeleteGroupWarningVisible,
      checkCanGroupBeRemoved,
      toggleConnectedByCategory
    };
  },
  defaultState
);

export default OrganisationIndexGroupPresenter;
